import {
  canonicalCrmChipMatchesFromText,
  canonicalCrmRecordHref,
  classifyCanonicalCrmMentionCandidates,
  resolveCanonicalCrmAnchor,
} from '../../../../shared/martinaCanonicalCrmAnchor';

/** Tipi anagrafici del picker CRM esistente: niente email/file/task nel composer. */
export const MARTINA_COMPOSER_MENTION_ENTITY_TYPES = [
  'contact',
  'company',
  'lead',
  'brand',
  'branch',
  'deal',
] as const;

export type MartinaComposerMentionEntityType = (typeof MARTINA_COMPOSER_MENTION_ENTITY_TYPES)[number];

/** Stessi campi di `CrmRecordAutocomplete`: riuso di POST /api/crm-query/execute. */
export const MARTINA_COMPOSER_SEARCH_FIELDS: Record<MartinaComposerMentionEntityType, string[]> = {
  // Una menzione identifica il record dal suo nome. Cercare anche in ruolo,
  // email, settore o tag produce candidati che contengono la parola ma non
  // sono la persona/azienda digitata (es. risultati casuali per `@Ines`).
  company: ['name'],
  contact: ['fullName'],
  branch: ['name'],
  lead: ['fullName'],
  deal: ['name'],
  brand: ['name'],
};

export type MartinaComposerAnchor = {
  tag: string;
  entityRef: string;
  entityType: string;
  id: string;
  title: string;
};

export type MartinaComposerSegment =
  | { kind: 'text'; value: string }
  | { kind: 'chip'; anchor: MartinaComposerAnchor; href: string };

export type MartinaComposerCrmHit = {
  type: string;
  id: string;
  title: string;
  /** Distintivo CRM (azienda, ruolo, …) per omonimia nel picker. */
  subtitle?: string;
  href?: string;
};

/** Sottotitolo muted per disambiguare omonimi (azienda contatto/lead, ecc.). */
export function composerMentionDisambiguationLabel(hit: MartinaComposerCrmHit): string | null {
  const subtitle = String(hit.subtitle ?? '').trim();
  return subtitle || null;
}

/** Difesa locale contro risposte remote troppo ampie o fuori ordine. */
export function composerMentionQueryMatchesTitle(query: string, title: string): boolean {
  const compact = (value: string) => String(value ?? '').normalize('NFC').replace(/[^\p{L}\p{N}]+/gu, '').toLocaleLowerCase('it-IT');
  const tokens = (value: string) => (String(value ?? '').normalize('NFC').match(/[\p{L}\p{N}]+/gu) ?? [])
    .map((token) => compact(token)).filter(Boolean);
  const queryCompact = compact(query);
  const titleCompact = compact(title);
  if (!queryCompact || !titleCompact) return false;
  const queryTokens = tokens(query);
  const titleTokens = tokens(title);
  // Il match e' per inizio parola: evita che `@Ines` trovi `Business` solo
  // perche' la sequenza di lettere e' contenuta nel mezzo di un altro termine.
  return queryTokens.length > 1
    ? queryTokens.every((token) => titleTokens.some((titleToken) => titleToken.startsWith(token)))
    : titleTokens.some((titleToken) => titleToken.startsWith(queryCompact));
}

export type MartinaComposerUniqueness =
  | { status: 'unique'; hit: MartinaComposerCrmHit; anchor: MartinaComposerAnchor; href: string }
  | { status: 'ambiguous'; hits: MartinaComposerCrmHit[] }
  | { status: 'none' };

const NAME_LEADER_STOP = new Set([
  'chi', 'che', 'cosa', 'quali', 'quale', 'quanto', 'quanti', 'come', 'dove', 'quando',
  'perche', 'perché', 'nelle', 'nella', 'nello', 'nel', 'nei', 'negli', 'dai', 'dal',
  'ciao', 'buongiorno', 'buonasera', 'salve', 'martina', 'dimmi', 'mostrami', 'preparami',
  'cerca', 'cercami', 'trova', 'trovami', 'elenca', 'una', 'uno', 'un', 'il', 'lo', 'la',
  'i', 'gli', 'le', 'oggi', 'domani',
]);

export function composerSegmentsFromPlainText(text: string): MartinaComposerSegment[] {
  const source = String(text ?? '');
  const matches = canonicalCrmChipMatchesFromText(source);
  if (!matches.length) return [{ kind: 'text', value: source }];
  const segments: MartinaComposerSegment[] = [];
  let cursor = 0;
  for (const match of matches) {
    if (match.start > cursor) segments.push({ kind: 'text', value: source.slice(cursor, match.start) });
    segments.push({
      kind: 'chip',
      anchor: { tag: match.tag, entityRef: match.entityRef, entityType: match.type, id: match.id, title: match.title },
      href: match.href,
    });
    cursor = match.end;
  }
  if (cursor < source.length) segments.push({ kind: 'text', value: source.slice(cursor) });
  return segments.length ? segments : [{ kind: 'text', value: source }];
}

export function composerTrailingText(segments: MartinaComposerSegment[]): string {
  const last = segments[segments.length - 1];
  return last?.kind === 'text' ? last.value : '';
}

export function setComposerTrailingText(
  segments: MartinaComposerSegment[],
  value: string,
): MartinaComposerSegment[] {
  if (segments.length === 0) return composerSegmentsFromPlainText(value);
  const last = segments[segments.length - 1];
  if (last.kind === 'text') return [...segments.slice(0, -1), { kind: 'text', value }];
  return [...segments, { kind: 'text', value }];
}

/** Token visibile social-style: `@Nome Cognome`, mai il tag compatto `@AndreaAiello`. */
export function composerMentionDisplayToken(title: string): string {
  const trimmed = String(title ?? '').trim();
  return trimmed ? `@${trimmed}` : '';
}

/** Testo visibile inline: testo piano + `@Nome` per le menzioni collegate. */
export function displayComposerText(segments: MartinaComposerSegment[]): string {
  return segments.map((segment) => (
    segment.kind === 'text' ? segment.value : composerMentionDisplayToken(segment.anchor.title)
  )).join('');
}

export type ComposerMentionRange = {
  start: number;
  end: number;
  index: number;
  entityRef: string;
};

/** Range dei token `@Nome` nel display, per Backspace/Delete a unità. */
export function composerMentionRanges(segments: MartinaComposerSegment[]): ComposerMentionRange[] {
  const ranges: ComposerMentionRange[] = [];
  let offset = 0;
  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index]!;
    if (segment.kind === 'text') {
      offset += segment.value.length;
      continue;
    }
    const token = composerMentionDisplayToken(segment.anchor.title);
    ranges.push({
      start: offset,
      end: offset + token.length,
      index,
      entityRef: segment.anchor.entityRef,
    });
    offset += token.length;
  }
  return ranges;
}

/**
 * Ricostruisce i segmenti da un'unica stringa display: i token `@Nome` noti
 * restano chip CRM; il resto è testo. Se un token viene rotto/cancellato, il
 * collegamento crm:type:id sparisce (nessuna allucinazione di id).
 */
export function reconcileComposerSegmentsFromDisplay(
  knownMentions: Array<Extract<MartinaComposerSegment, { kind: 'chip' }>>,
  display: string,
): MartinaComposerSegment[] {
  const remaining = [...knownMentions];
  const result: MartinaComposerSegment[] = [];
  let cursor = 0;
  const source = String(display ?? '');
  while (cursor < source.length) {
    let matched: { chip: Extract<MartinaComposerSegment, { kind: 'chip' }>; remainingIndex: number; token: string } | null = null;
    for (let remainingIndex = 0; remainingIndex < remaining.length; remainingIndex += 1) {
      const chip = remaining[remainingIndex]!;
      const token = composerMentionDisplayToken(chip.anchor.title);
      if (token && source.startsWith(token, cursor)) {
        matched = { chip, remainingIndex, token };
        break;
      }
    }
    if (matched) {
      result.push(matched.chip);
      remaining.splice(matched.remainingIndex, 1);
      cursor += matched.token.length;
      continue;
    }
    let nextMentionAt = -1;
    for (const chip of remaining) {
      const token = composerMentionDisplayToken(chip.anchor.title);
      if (!token) continue;
      const idx = source.indexOf(token, cursor);
      if (idx >= 0 && (nextMentionAt < 0 || idx < nextMentionAt)) nextMentionAt = idx;
    }
    const end = nextMentionAt < 0 ? source.length : nextMentionAt;
    if (end > cursor) {
      result.push({ kind: 'text', value: source.slice(cursor, end) });
      cursor = end;
      continue;
    }
    result.push({ kind: 'text', value: source.slice(cursor, cursor + 1) });
    cursor += 1;
  }
  return mergeAdjacentComposerText(result.length ? result : composerSegmentsFromPlainText(''));
}

/** Payload del turno: nome + tag interno + `crm:type:id`. La UI non lo mostra. */
export function serializeComposerPayload(segments: MartinaComposerSegment[]): string {
  return segments.map((segment) => {
    if (segment.kind === 'text') return segment.value;
    return `${segment.anchor.title} ${segment.anchor.tag} ${segment.anchor.entityRef}`;
  }).join('').replace(/[ \t]+\n/g, '\n').replace(/  +/g, ' ');
}

function escapeComposerHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** HTML sicuro per CRM/email: ogni menzione mantiene il collegamento canonico. */
export function composerSegmentsToLinkedHtml(segments: MartinaComposerSegment[]): string {
  return segments.map((segment) => {
    if (segment.kind === 'text') return escapeComposerHtml(segment.value).replace(/\n/g, '<br>');
    const href = escapeComposerHtml(segment.href);
    const title = escapeComposerHtml(composerMentionDisplayToken(segment.anchor.title));
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" data-crm-entity-ref="${escapeComposerHtml(segment.anchor.entityRef)}" style="color:#2563eb;text-decoration:underline">${title}</a>`;
  }).join('');
}

export function stripComposerTransportForDisplay(text: string): string {
  return String(text ?? '')
    .replace(/\s*@[^\s]+/g, '')
    .replace(/\s*crm:[a-z_]+:[A-Za-z0-9_-]+/g, '')
    .replace(/  +/g, ' ')
    .trim();
}

export function composerHrefForHit(hit: MartinaComposerCrmHit): string | null {
  return canonicalCrmRecordHref(hit.type, hit.id, hit.href);
}

/**
 * Scelta esplicita dal picker: non rieseguire il classify sul titolo
 * (evita none/ambiguo). Solo inserimento chip.
 */
export function uniquenessFromSelectedComposerHit(
  hit: MartinaComposerCrmHit,
): Extract<MartinaComposerUniqueness, { status: 'unique' }> | null {
  const canonical = resolveCanonicalCrmAnchor({
    status: 'none',
    candidates: [hit],
  });
  const href = composerHrefForHit(hit);
  if (!canonical || !href) return null;
  return {
    status: 'unique',
    hit,
    anchor: {
      tag: canonical.tag,
      entityRef: canonical.entityRef,
      entityType: canonical.entityType,
      id: canonical.id,
      title: canonical.title,
    },
    href,
  };
}

export function resolveComposerMentionUniqueness(
  query: string,
  hits: MartinaComposerCrmHit[],
): MartinaComposerUniqueness {
  const classified = classifyCanonicalCrmMentionCandidates(query, hits);
  if (classified.status === 'none') return { status: 'none' };
  if (classified.status === 'ambiguous') {
    return { status: 'ambiguous', hits: classified.candidates };
  }
  const hit = classified.candidate;
  const canonical = resolveCanonicalCrmAnchor({
    status: 'none',
    candidates: [hit],
  });
  const href = composerHrefForHit(hit);
  if (!canonical || !href) return { status: 'none' };
  return {
    status: 'unique',
    hit,
    anchor: {
      tag: canonical.tag,
      entityRef: canonical.entityRef,
      entityType: canonical.entityType,
      id: canonical.id,
      title: canonical.title,
    },
    href,
  };
}

export function buildComposerMentionQuery(
  entityType: MartinaComposerMentionEntityType,
  search: string,
): {
  entityType: MartinaComposerMentionEntityType;
  match: 'all';
  filters: [];
  filterGroup: {
    match: 'any';
    filters: Array<{ field: string; operator: 'contains'; value: string }>;
    groups: [];
  };
  sorts: [];
  limit: number;
  offset: 0;
} {
  const fields = MARTINA_COMPOSER_SEARCH_FIELDS[entityType] ?? ['name'];
  return {
    entityType,
    match: 'all',
    filters: [],
    filterGroup: {
      match: 'any',
      filters: fields.map((field) => ({ field, operator: 'contains', value: search })),
      groups: [],
    },
    sorts: [],
    limit: 8,
    offset: 0,
  };
}

export type ComposerAtQuery = { query: string; start: number; end: number };

/** Scorciatoia opzionale: `@` apre l'autocomplete, non è il formato visibile. */
export function extractComposerAtQuery(text: string): ComposerAtQuery | null {
  const match = String(text ?? '').match(/@([\p{L}\p{N}'’.-]*(?:\s+[\p{L}\p{N}'’.-]*){0,3})$/u);
  if (!match || match.index == null) return null;
  return { query: match[1] ?? '', start: match.index, end: String(text).length };
}

/**
 * `@` attivo per il picker: ignora i token menzione già collegati
 * (es. caret subito dopo `@Giampiero Di Maio` già risolto).
 */
export function activeComposerAtQuery(
  segments: MartinaComposerSegment[],
  caret?: number,
): ComposerAtQuery | null {
  const display = displayComposerText(segments);
  const sliceEnd = caret == null ? display.length : Math.max(0, Math.min(caret, display.length));
  const at = extractComposerAtQuery(display.slice(0, sliceEnd));
  if (!at || !at.query.trim()) return null;
  const ranges = composerMentionRanges(segments);
  if (ranges.some((range) => range.start === at.start && at.end <= range.end)) return null;
  return at;
}

/** Nome completo appena digitato: il prefisso `@` è una scorciatoia, non un obbligo. */
export function activeComposerPlainNameQuery(
  segments: MartinaComposerSegment[],
  caret?: number,
): ComposerNameCandidate | null {
  const display = displayComposerText(segments);
  const sliceEnd = caret == null ? display.length : Math.max(0, Math.min(caret, display.length));
  const prefix = display.slice(0, sliceEnd);
  const candidate = extractComposerNameCandidates(prefix).at(-1) ?? null;
  if (!candidate || candidate.end !== prefix.length) return null;
  if (composerMentionRanges(segments).some((range) => candidate.start >= range.start && candidate.end <= range.end)) return null;
  return candidate;
}

export type ComposerNameCandidate = { text: string; start: number; end: number };

export function extractComposerNameCandidates(text: string): ComposerNameCandidate[] {
  const source = String(text ?? '');
  const candidates: ComposerNameCandidate[] = [];
  const pattern = /\b([A-ZÀ-ÖØ-Ý][\p{L}'’.-]*(?:\s+[A-ZÀ-ÖØ-Ý][\p{L}'’.-]*){1,3})\b/gu;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(source))) {
    const value = match[1]?.trim() ?? '';
    const first = value.split(/\s+/)[0]?.toLocaleLowerCase('it-IT') ?? '';
    if (!value || NAME_LEADER_STOP.has(first)) continue;
    candidates.push({ text: value, start: match.index, end: match.index + match[0].length });
  }
  return candidates;
}

export function primaryComposerNameQuery(text: string): string | null {
  const at = extractComposerAtQuery(text);
  if (at && at.query.trim().length >= 2) return at.query.trim();
  const names = extractComposerNameCandidates(text);
  return names.length ? names[names.length - 1]!.text : null;
}

export function applyUniqueMentionToSegments(
  segments: MartinaComposerSegment[],
  name: string,
  uniqueness: Extract<MartinaComposerUniqueness, { status: 'unique' }>,
  caret?: number,
): MartinaComposerSegment[] {
  if (segments.some((segment) => segment.kind === 'chip' && segment.anchor.entityRef === uniqueness.anchor.entityRef)) {
    return segments;
  }
  const display = displayComposerText(segments);
  const sliceEnd = caret == null ? display.length : Math.max(0, Math.min(caret, display.length));
  const beforeCaret = display.slice(0, sliceEnd);
  const at = extractComposerAtQuery(beforeCaret);
  let start: number;
  let end: number;
  if (at && at.query.trim().length > 0) {
    start = at.start;
    end = at.end;
  } else {
    const idx = beforeCaret.toLocaleLowerCase('it-IT').lastIndexOf(name.toLocaleLowerCase('it-IT'));
    if (idx < 0) {
      start = sliceEnd;
      end = sliceEnd;
    } else {
      start = idx;
      end = idx + name.length;
    }
  }
  const before = display.slice(0, start);
  let after = display.slice(end);
  // Spazio solo se si continua a digitare a fine riga; non spezzare la punteggiatura.
  if (!after) after = ' ';
  const token = composerMentionDisplayToken(uniqueness.anchor.title);
  const newDisplay = `${before}${token}${after}`;
  const newChip: Extract<MartinaComposerSegment, { kind: 'chip' }> = {
    kind: 'chip',
    anchor: uniqueness.anchor,
    href: uniqueness.href,
  };
  const known = [
    ...segments.filter((segment): segment is Extract<MartinaComposerSegment, { kind: 'chip' }> => segment.kind === 'chip'),
    newChip,
  ];
  return reconcileComposerSegmentsFromDisplay(known, newDisplay);
}

function mergeAdjacentComposerText(segments: MartinaComposerSegment[]): MartinaComposerSegment[] {
  const merged: MartinaComposerSegment[] = [];
  for (const segment of segments) {
    const last = merged[merged.length - 1];
    if (segment.kind === 'text' && last?.kind === 'text') {
      merged[merged.length - 1] = { kind: 'text', value: `${last.value}${segment.value}` };
      continue;
    }
    merged.push(segment);
  }
  return merged.length ? merged : composerSegmentsFromPlainText('');
}

/** Rimuove del tutto la menzione (token + ref CRM), come Backspace social. */
export function removeComposerChip(
  segments: MartinaComposerSegment[],
  target: { entityRef: string; index: number },
): MartinaComposerSegment[] {
  const chip = segments[target.index];
  if (chip?.kind !== 'chip' || chip.anchor.entityRef !== target.entityRef) return segments;
  return mergeAdjacentComposerText([
    ...segments.slice(0, target.index),
    ...segments.slice(target.index + 1),
  ]);
}

/**
 * Backspace a fine token / Delete a inizio token (o caret dentro il token):
 * cancella l'intera menzione inline, senza pulsante X.
 */
export function deleteComposerMentionAtCaret(
  segments: MartinaComposerSegment[],
  caret: number,
  mode: 'backspace' | 'delete',
): { segments: MartinaComposerSegment[]; caret: number } | null {
  const ranges = composerMentionRanges(segments);
  for (const range of ranges) {
    const hitBackspace = mode === 'backspace' && caret > range.start && caret <= range.end;
    const hitDelete = mode === 'delete' && caret >= range.start && caret < range.end;
    if (!hitBackspace && !hitDelete) continue;
    return {
      segments: removeComposerChip(segments, {
        entityRef: range.entityRef,
        index: range.index,
      }),
      caret: range.start,
    };
  }
  return null;
}

export type VoiceAutoSendDecision =
  | { action: 'send'; segments: MartinaComposerSegment[] }
  | { action: 'block_ambiguous'; candidates: MartinaComposerCrmHit[]; segments: MartinaComposerSegment[] }
  | { action: 'send_raw'; segments: MartinaComposerSegment[] }
  | { action: 'block_pending' };

export function decideVoiceAutoSend(input: {
  transcript: string;
  uniqueness: MartinaComposerUniqueness | { status: 'pending' };
}): VoiceAutoSendDecision {
  const segments = composerSegmentsFromPlainText(input.transcript);
  const query = primaryComposerNameQuery(input.transcript);
  if (input.uniqueness.status === 'pending') return { action: 'block_pending' };
  if (!query) return { action: 'send_raw', segments };
  if (input.uniqueness.status === 'ambiguous') {
    return { action: 'block_ambiguous', candidates: input.uniqueness.hits, segments };
  }
  if (input.uniqueness.status === 'unique') {
    return {
      action: 'send',
      segments: applyUniqueMentionToSegments(segments, query, input.uniqueness),
    };
  }
  return { action: 'send_raw', segments };
}
